import { autoinject, bindingMode, bindable } from 'aurelia-framework';
import { ComponentBase, Sorting, StandardLoadOptions, SortDirection, State } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { CorrespondenceService } from 'app/core/services/correspondence-service';
import { apiCall } from 'app/shared/lib/api/api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import * as Core from 'app/core/models/essette-api';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';


@autoinject
export class CorrespondenceHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected targetId!: string;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected pageSize: number = 25;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected readonly: boolean = true;

    protected canPreviewDownloads: boolean = false;

    protected letters: Core.CorrespondenceLetter[] = [];
    protected stateRetry: State = new State();
    protected sorting = new Sorting();
    protected resultCount: number = 0;

    protected correspondenceConfig: ICorrespondenceConfig = {
        transitionalPageId: '604',
        moduleCode: 'UM',
        serviceRootUrl: 'um/auths',
        rollupRootUrl: 'um/auths/correspondence',
        targetType: 'UM.Auth',
        targetColumnHeader: 'Auth #',
        targetColumnProperty: 'auth.number',
        targetInclude: ['auth', 'task', 'attachedTo.type'],
        previewTransitionalPageId: '4054',
        rollupByMember: false,
        targetSummaryTooltip: 'um:auth.summary.$long'
    }


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CorrespondenceService,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.targetId) { throw new Error('Bindable property [target-id] was not set'); }
        this.service.configure(this.correspondenceConfig);
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected pageSizeChanged(): void {
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadHistoryLetters();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadHistoryLetters(): Promise<void> {
        if(!this.targetId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0
        };

        const includes: string[] = [
            'template',
            'template.version',
            'template.version.language',
            'status',
            'copies',
            'copies.deliveryMethod',
            'copies.delivered.recipient',
            'copies.status',
            'copies.coverSheet.templateVersion.template',
            'metadata.modified.byUser'
        ];

        try {
            this.state.beginLoad();

            let response;
            response = await this.service.loadLettersByTarget(this.targetId, true, options, includes, true);
            this.letters = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
